import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../app/store";
import { Unit, findUnit, unitsFor } from "../../data/units";
import type { SignLanguageId } from "../../data/signLanguages";
import { addDays, daysBetween, today, weekdayIndex } from "./dates";

export interface DayAttempts {
  date: string;
  correct: number;
  total: number;
}

export interface ProgressState {
  xp: number;
  dailyXp: number;
  dailyGoal: number;
  lastActiveDate: string | null;
  streak: number;
  unitProgress: Record<string, number>;
  attempts: DayAttempts[];
  /** Right/total attempts per letter, keyed "<language>:<letter id>". Missing in progress saved by older versions. */
  letterStats?: Record<string, { correct: number; total: number }>;
}

export const initialProgress: ProgressState = {
  xp: 0,
  dailyXp: 0,
  dailyGoal: 50,
  lastActiveDate: null,
  streak: 0,
  unitProgress: {},
  attempts: [],
  letterStats: {},
};

/** Moves the "active day" forward; ignores dates earlier than the last active day. */
function touchDay(state: ProgressState, date: string) {
  if (state.lastActiveDate === date) return;
  if (state.lastActiveDate !== null) {
    const gap = daysBetween(state.lastActiveDate, date);
    if (gap < 0) return;
    state.streak = gap === 1 ? state.streak + 1 : 1;
  } else {
    state.streak = 1;
  }
  state.lastActiveDate = date;
  state.dailyXp = 0;
}

const progressSlice = createSlice({
  name: "progress",
  initialState: initialProgress,
  reducers: {
    awardXp: {
      reducer(state, action: PayloadAction<{ amount: number; date: string }>) {
        const { amount, date } = action.payload;
        if (!(amount > 0)) return;
        const backwards =
          state.lastActiveDate !== null && daysBetween(state.lastActiveDate, date) < 0;
        touchDay(state, date);
        state.xp += amount;
        if (!backwards) state.dailyXp += amount;
      },
      prepare(amount: number, date: string = today()) {
        return { payload: { amount, date } };
      },
    },
    completeLesson: {
      reducer(state, action: PayloadAction<{ unitId: string; date: string }>) {
        const unit = findUnit(action.payload.unitId);
        if (!unit || unit.comingSoon) return;
        const done = state.unitProgress[unit.id] ?? 0;
        state.unitProgress[unit.id] = Math.min(unit.lessons, done + 1);
      },
      prepare(unitId: string, date: string = today()) {
        return { payload: { unitId, date } };
      },
    },
    recordAttempt: {
      reducer(
        state,
        action: PayloadAction<{ correct: boolean; date: string; letter?: { language: SignLanguageId; id: string } }>
      ) {
        const { correct, date, letter } = action.payload;
        if (letter) {
          state.letterStats ??= {};
          const key = `${letter.language}:${letter.id}`;
          const stat = (state.letterStats[key] ??= { correct: 0, total: 0 });
          stat.total += 1;
          if (correct) stat.correct += 1;
        }
        let day = state.attempts.find((d) => d.date === date);
        if (!day) {
          day = { date, correct: 0, total: 0 };
          state.attempts.push(day);
        }
        day.total += 1;
        if (correct) day.correct += 1;
        state.attempts = state.attempts.filter((d) => {
          const age = daysBetween(d.date, date);
          return age >= 0 ? age < 7 : true;
        });
      },
      prepare(correct: boolean, date: string = today(), letter?: { language: SignLanguageId; id: string }) {
        return { payload: { correct, date, letter } };
      },
    },
  },
});

export const { awardXp, completeLesson, recordAttempt } = progressSlice.actions;
export default progressSlice.reducer;

export const selectProgress = (state: RootState): ProgressState => state.progress;

export type UnitStatus = "done" | "current" | "locked" | "comingSoon";
export type UnitView = Unit & { status: UnitStatus; lessonsDone: number };

export function unitViews(p: ProgressState, language: SignLanguageId): UnitView[] {
  let previousDone = true;
  return unitsFor(language).map((u) => {
    const lessonsDone = p.unitProgress[u.id] ?? 0;
    let status: UnitStatus;
    if (u.comingSoon) status = "comingSoon";
    else if (lessonsDone >= u.lessons) status = "done";
    else if (previousDone) status = "current";
    else status = "locked";
    if (!u.comingSoon) previousDone = status === "done";
    return { ...u, status, lessonsDone };
  });
}

export function currentStreak(p: ProgressState, date: string): number {
  if (p.lastActiveDate === null) return 0;
  const gap = daysBetween(p.lastActiveDate, date);
  return gap <= 1 ? p.streak : 0;
}

export interface DayDot {
  label: string;
  date: string;
  active: boolean;
  isToday: boolean;
}

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

export function weekActivity(p: ProgressState, date: string): DayDot[] {
  const monday = addDays(date, -weekdayIndex(date));
  const streak = currentStreak(p, date);
  return DAY_LABELS.map((label, i) => {
    const d = addDays(monday, i);
    let active = false;
    if (p.lastActiveDate !== null && streak > 0) {
      const back = daysBetween(d, p.lastActiveDate);
      active = back >= 0 && back < streak;
    }
    return { label, date: d, active, isToday: d === date };
  });
}

export function weeklyAccuracy(p: ProgressState, date: string): number | null {
  let correct = 0;
  let total = 0;
  for (const d of p.attempts) {
    const age = daysBetween(d.date, date);
    if (age >= 0 && age < 7) {
      correct += d.correct;
      total += d.total;
    }
  }
  return total === 0 ? null : correct / total;
}

export function dailyGoalProgress(p: ProgressState, date: string): number {
  return p.lastActiveDate === date ? p.dailyXp : 0;
}

export type MasteryLevel = "new" | "learning" | "mastered";

/** Mastered = 3+ attempts at 80%+ right; learning = tried; new = never tried. */
export function letterMastery(p: ProgressState, language: SignLanguageId, letterIds: string[]) {
  return letterIds.map((id) => {
    const { correct, total } = p.letterStats?.[`${language}:${id}`] ?? { correct: 0, total: 0 };
    const level: MasteryLevel = total === 0 ? "new" : total >= 3 && correct / total >= 0.8 ? "mastered" : "learning";
    return { id, level, correct, total };
  });
}

/** Letters tried but not yet mastered, lowest accuracy first (most attempts first on a tie). */
export function weakLetters(p: ProgressState, language: SignLanguageId, letterIds: string[]): string[] {
  return letterMastery(p, language, letterIds)
    .filter((m) => m.level === "learning")
    .sort((a, b) => a.correct / a.total - b.correct / b.total || b.total - a.total)
    .map((m) => m.id);
}
